import { useLazyQuery, useQuery } from '@apollo/client';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import HandleQuery from '@components/HandleQuery';
import { bannerContext } from '@context/Contexts';
import useSubscribeToJourney from '@hooks/subscriptions/useSubscribeToJourney';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import {
  ExpertGlobalRoleEnum,
  ExpertRoleCreateDocument,
  ExpertRoleCreateMutation,
  ExpertRoleCreateMutationVariables,
  ExpertRoleDestroyDocument,
  ExpertRoleDestroyMutation,
  ExpertRoleDestroyMutationVariables,
  ExpertRoleRoleEnum,
  GetExpertRoleDocument,
  GetExpertRoleQuery,
  GetExpertRoleQueryVariables,
  GetJourneyWithExpertDocument,
  GetJourneyWithExpertQuery,
  GetJourneyWithExpertQueryVariables
} from '@typing/Generated';
import { primaryExpertRole } from '@utils/modelUtils';

type Props = {
  children: ReactNode;
  journeyId: string;
};

const ExpertJourneyBannerProvider = ({ children, journeyId }: Props) => {
  const myExpert = useMyExpert();
  const [submitting, setSubmitting] = useState(false);

  const [getRole, getRoleQuery] = useLazyQuery<GetExpertRoleQuery, GetExpertRoleQueryVariables>(GetExpertRoleDocument);

  useEffect(() => {
    if (myExpert) {
      getRole({ variables: { expertId: myExpert?.id, journeyId } });
    }
  }, [getRole, journeyId, myExpert]);

  const getJourneyQuery = useQuery<GetJourneyWithExpertQuery, GetJourneyWithExpertQueryVariables>(
    GetJourneyWithExpertDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  const expertRole = useMemo(() => getRoleQuery.data?.expertRole, [getRoleQuery.data?.expertRole]);

  const [createExpertRole, client] = useMutationWithValidation<
    ExpertRoleCreateMutation,
    ExpertRoleCreateMutationVariables
  >(ExpertRoleCreateDocument, 'expertRole', {
    update: (_store, { data }) => {
      if (data?.expertRoleCreate?.expertRole) {
        client.resetStore();
      }
    }
  });

  const [destroyExpertRole, apolloClient] = useMutationWithValidation<
    ExpertRoleDestroyMutation,
    ExpertRoleDestroyMutationVariables
  >(ExpertRoleDestroyDocument, 'expertRole', {
    update: (_store, { data }) => {
      const currentExpertRole = expertRole;
      if (data?.expertRoleDestroy && currentExpertRole) {
        apolloClient.resetStore();
      }
    }
  });

  const handleExpertRoleCreate = useCallback(() => {
    if (myExpert?.id) {
      setSubmitting(true);
      createExpertRole({
        expertId: myExpert?.id,
        journeyId,
        role: ExpertRoleRoleEnum.team_member
      });
    }
  }, [createExpertRole, journeyId, myExpert?.id]);

  const intl = useIntl();

  const handleExpertRoleDestroy = useCallback(() => {
    setSubmitting(true);
    if (expertRole) {
      destroyExpertRole({
        id: expertRole.id
      });
    }
  }, [destroyExpertRole, expertRole]);

  // If the expert is a backup, we want this to be false, not undefined
  const expertInJourney = useMemo(() => {
    if (
      (myExpert && myExpert.expertGlobalRole === ExpertGlobalRoleEnum.manager) ||
      expertRole?.role === ExpertRoleRoleEnum.backup
    ) {
      if (expertRole && expertRole.role !== ExpertRoleRoleEnum.backup) {
        return true;
      }
      return false;
    }
  }, [myExpert, expertRole]);

  useEffect(() => {
    setSubmitting(false);
  }, [expertInJourney]);

  const primaryExpert = useMemo(
    () => primaryExpertRole(getJourneyQuery.data?.journey.expertRoles)?.expert,
    [getJourneyQuery.data?.journey.expertRoles]
  );

  const { changeCurrentBanner, currentBanner, deleteCurrentBanner } = useContext(bannerContext);

  useEffect(() => {
    if (expertInJourney !== undefined && primaryExpert && primaryExpert?.id !== myExpert?.id && !currentBanner) {
      changeCurrentBanner({
        buttonDisabled: submitting,
        buttonOnClick: expertInJourney ? handleExpertRoleDestroy : handleExpertRoleCreate,
        buttonTextKey: expertInJourney ? 'pages.expert.banners.leaveJourney' : 'pages.expert.banners.joinJourney',
        color: 'bannerPink',
        journeyId,
        position: 0,
        text: expertInJourney
          ? intl.formatMessage(
              { id: 'pages.expert.banners.viewingAClientJoined' },
              { expertName: primaryExpert?.fullName }
            )
          : intl.formatMessage(
              { id: 'pages.expert.banners.viewingAClientNotJoined' },
              { expertName: primaryExpert?.fullName }
            )
      });
    }
    return () => {
      if (currentBanner?.journeyId) {
        deleteCurrentBanner();
      }
    };
  }, [
    changeCurrentBanner,
    currentBanner,
    deleteCurrentBanner,
    expertInJourney,
    handleExpertRoleCreate,
    handleExpertRoleDestroy,
    intl,
    journeyId,
    myExpert?.id,
    primaryExpert,
    primaryExpert?.fullName,
    primaryExpert?.id,
    submitting
  ]);

  return (
    <HandleQuery hideLoader query={getRoleQuery}>
      {() => <>{children}</>}
    </HandleQuery>
  );
};

export default ExpertJourneyBannerProvider;
